import { useState, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { InputText } from 'primereact/inputtext';
import { Card } from 'primereact/card';
import { Badge } from 'primereact/badge';
import { Dropdown } from 'primereact/dropdown';
import { COUNTRY_OPTIONS } from '../CountryPagesManager/countrySectionCatalog';

export default function CountryPagesList() {
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState(null);
  const navigate = useNavigate();
  const public_url = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';

  useEffect(() => {
    api
      .get('/country_pages/summary')
      .then((response) => {
        setPages(response.data || []);
        setLoadingPages(false);
      })
      .catch((error) => {
        console.error('Error fetching country pages:', error);
        setLoadingPages(false);
      });
  }, []);

  const pagesFiltered = useMemo(() => {
    let filtered = [...pages];
    if (countryFilter) {
      filtered = filtered.filter((p) => p.country === countryFilter);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          (p.full_slug && p.full_slug.toLowerCase().includes(q)) ||
          (p.title && p.title.toLowerCase().includes(q)) ||
          (p.hero?.title && p.hero.title.toLowerCase().includes(q)) ||
          (p.country && p.country.toLowerCase().includes(q))
      );
    }
    return filtered;
  }, [pages, searchTerm, countryFilter]);

  return (
    <Card>
      <div className="card p-4">
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>Country pages</span>
              <Badge
                className="ml-2 b-4 relative"
                value={pagesFiltered.length ?? 0}
                style={{ bottom: '3px', backgroundColor: '#2020A0' }}
              />
            </h2>
          </div>
          <div className="col-6 flex justify-content-end">
            <Button
              label="Add country page"
              icon="pi pi-plus"
              className="p-button-primary p-button-sm secondary-button"
              style={{ minWidth: 160 }}
              onClick={() => navigate('/country_pages/new')}
            />
          </div>
        </div>

        <div className="grid mb-4">
          <div className="col-12 flex gap-2 align-items-end">
            <span style={{ minWidth: 180 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Country</strong>
              </label>
              <Dropdown
                value={countryFilter}
                options={COUNTRY_OPTIONS}
                showClear
                onChange={(e) => setCountryFilter(e.value)}
                optionLabel="name"
                optionValue="code"
                placeholder="All countries"
                className="w-full"
              />
            </span>
            <div className="flex flex-column" style={{ minWidth: 250 }}>
              <label style={{ fontSize: '0.95rem' }}>
                <strong>Search</strong>
              </label>
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full p-button-sm"
                placeholder="Search by URL or title"
                style={{ fontSize: '0.95rem' }}
              />
            </div>
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-text text-danger p-button-sm"
              onClick={() => {
                setSearchTerm('');
                setCountryFilter(null);
              }}
            />
          </div>
        </div>

        {loadingPages && (
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading country pages...
            </div>
          </div>
        )}

        {!loadingPages && (
          <DataTable
            value={pagesFiltered}
            emptyMessage="No country pages yet. Create /en/mexico, /es/mexico, /en/canada, /es/canada."
            className="p-datatable-sm"
            showGridlines
            stripedRows
          >
            <Column
              field="full_slug"
              header="URL"
              body={(rowData) => (
                <strong>
                  <a
                    href={`${public_url}${rowData.full_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {rowData.full_slug}
                  </a>
                </strong>
              )}
            />
            <Column
              field="country"
              header="Country"
              body={(row) => row.country || '—'}
            />
            <Column field="lang" header="Lang" style={{ width: 80 }} />
            <Column
              header="Title"
              body={(row) => row.hero?.title || row.title || '—'}
            />
            <Column
              header="Progress"
              body={(row) => `${row.completionPercentage ?? 0}%`}
              style={{ width: 100 }}
            />
            <Column
              header="Published"
              body={(row) => (row.revalidated ? 'Yes' : 'No')}
              style={{ width: 100 }}
            />
            <Column
              header=""
              style={{ width: 100 }}
              body={(row) => (
                <Button
                  icon="pi pi-pencil"
                  className="p-button-text p-button-sm"
                  onClick={() => navigate(`/country_pages/edit/${row._id}`)}
                />
              )}
            />
          </DataTable>
        )}
      </div>
    </Card>
  );
}
